import type { CheckoutMethod, CheckoutPhase, PayerPreview, PaymentSession, PaymentSessionPayer } from '../types';
import type { ResolvedCheckoutUiOptions } from '../config/checkout-policy';
import { resolveFallbackMethodAvailability } from '../config/checkout-policy';
import type { AppHandoffStatus } from '../app/handoff';
import { APP_STORE_LINKS } from '../config/app-links';
import { renderAppleStoreIcon, renderGooglePlayIcon } from './app-store-icons';
import { AppStoreDownloadModal, type AppStorePlatform } from './app-store-modal';
import { renderPaymentQrMarkup } from '../qr/render-qr';
import { renderSecureLockSvg } from '../qr/zippy-logo';
import { getPayWindowRemainingRatio, msUntil, shouldShowPayProgress } from '../session/timing';
import {
  formatAmount,
  formatCountdown,
  formatCountdownAccessible,
  formatDisplayInitials,
  outcomeTerminalCopy,
  TERMINAL_EXIT_COUNTDOWN_SEC,
  terminalExitLeadText,
  type TerminalExitMode,
} from '../utils';
import { CHECKOUT_STYLES } from '../theme';

export type UiCallbacks = {
  onChooseMethod: (method: CheckoutMethod) => void;
  onVerify: (query: string) => Promise<void>;
  onPay: (paymentId: string) => Promise<void>;
  onSelectPayer?: (payer: PayerPreview) => void;
  onBackToChooseMethod?: () => void;
  onBackToManualEntry?: () => void;
  onBackToMobileAppFallback?: () => void;
  onOpenMobileApp?: () => void;
  onInstallZippy?: () => void;
  onRetryMobileApp?: () => void;
  onPaymentIdChange?: (query: string) => void;
  onTerminalExit?: () => void;
};

export type UiState = {
  phase: CheckoutPhase;
  session: PaymentSession | null;
  sessionId: string;
  committedMethod: CheckoutMethod | null;
  paymentId: string;
  verifiedPayer: PayerPreview | null;
  verifyMatches: PayerPreview[] | null;
  errorMessage: string | null;
  loading: boolean;
  mode: 'inline' | 'modal';
  exitMode?: TerminalExitMode | null;
  appHandoffStatus?: AppHandoffStatus;
  mobileAppManualFallback?: boolean;
};

export class CheckoutUi {
  private readonly shadow: ShadowRoot;
  private readonly root: HTMLElement;
  private state: UiState;
  private readonly callbacks: UiCallbacks;
  private expiryTimer: ReturnType<typeof setInterval> | null = null;
  private terminalExitTimer: ReturnType<typeof setInterval> | null = null;
  private readonly appStoreModal: AppStoreDownloadModal;
  private uiOptions: ResolvedCheckoutUiOptions;

  constructor(
    host: HTMLElement,
    initialState: UiState,
    callbacks: UiCallbacks,
    uiOptions: ResolvedCheckoutUiOptions,
    extraStyles = '',
  ) {
    this.callbacks = callbacks;
    this.uiOptions = uiOptions;
    this.state = initialState;
    this.shadow = host.attachShadow({ mode: 'open' });
    this.appStoreModal = new AppStoreDownloadModal(this.shadow);
    const style = document.createElement('style');
    style.textContent = CHECKOUT_STYLES + extraStyles;
    this.shadow.appendChild(style);
    this.root = document.createElement('div');
    this.root.className = 'zippy-root';
    this.root.setAttribute('role', 'region');
    this.root.setAttribute('aria-label', 'Zippy Pay checkout');
    this.shadow.appendChild(this.root);
    this.render();
  }

  update(partial: Partial<UiState>): void {
    const next = { ...this.state, ...partial };
    if (this.shouldSkipRender(this.state, next)) {
      this.state = next;
      return;
    }
    this.state = next;
    this.render();
  }

  updateUiOptions(uiOptions: ResolvedCheckoutUiOptions): void {
    this.uiOptions = uiOptions;
    this.render();
  }

  private shouldSkipRender(prev: UiState, next: UiState): boolean {
    return this.renderSnapshot(prev) === this.renderSnapshot(next);
  }

  private renderSnapshot(state: UiState): string {
    const session = state.session;
    return JSON.stringify({
      phase: state.phase,
      sessionId: state.sessionId,
      committedMethod: state.committedMethod,
      paymentId: state.paymentId,
      errorMessage: state.errorMessage,
      loading: state.loading,
      mode: state.mode,
      exitMode: state.exitMode ?? null,
      appHandoffStatus: state.appHandoffStatus ?? 'idle',
      mobileAppManualFallback: state.mobileAppManualFallback ?? false,
      verifiedPayer: state.verifiedPayer,
      verifyMatches: state.verifyMatches,
      session: session
        ? {
            status: session.status,
            phase: session.phase,
            amount: session.amount,
            currency: session.currency,
            businessName: session.businessName,
            paymentExpiryAt: session.paymentExpiryAt,
            paymentStartAt: session.paymentStartAt,
            payer: session.payer,
            transactionStatus: session.transactionStatus,
            checkoutMethod: session.checkoutMethod,
            expiresAt: session.expiresAt,
            transactionId: session.transactionId,
          }
        : null,
    });
  }

  private restoreFocusState(): { id: string | null; selection: number | null } {
    const active = this.shadow.activeElement;
    if (active instanceof HTMLInputElement && active.id) {
      return { id: active.id, selection: active.selectionStart };
    }
    return { id: null, selection: null };
  }

  private applyFocusState(focus: { id: string | null; selection: number | null }): void {
    if (!focus.id) {
      return;
    }
    const element = this.shadow.getElementById(focus.id);
    if (!(element instanceof HTMLInputElement)) {
      return;
    }
    element.focus();
    if (focus.selection !== null) {
      element.setSelectionRange(focus.selection, focus.selection);
    }
  }

  destroy(): void {
    this.clearExpiryTimer();
    this.clearTerminalExitTimer();
    this.appStoreModal.destroy();
  }

  getShadowRoot(): ShadowRoot {
    return this.shadow;
  }

  private render(): void {
    const focusState = this.restoreFocusState();
    this.clearExpiryTimer();
    this.clearTerminalExitTimer();

    const {
      phase,
      session,
      errorMessage,
      loading,
      verifiedPayer,
      verifyMatches,
      paymentId,
      sessionId,
      committedMethod,
      exitMode,
      mode,
      appHandoffStatus = 'idle',
      mobileAppManualFallback = false,
    } = this.state;
    const isTerminal =
      phase === 'success' ||
      phase === 'expired' ||
      phase === 'cancelled' ||
      phase === 'declined' ||
      phase === 'failed' ||
      phase === 'invalid-session' ||
      phase === 'error';
    const showPayProgress =
      !isTerminal && shouldShowPayProgress(session, phase);
    const isQrPayFlow =
      phase === 'qr-active' ||
      (phase === 'awaiting' && committedMethod === 'qr');
    const isMobileAppFlow =
      phase === 'mobile-app-active' ||
      (phase === 'awaiting' && committedMethod === 'mobileApp');

    this.root.className =
      showPayProgress
        ? isQrPayFlow
          ? 'zippy-root zippy-root--paying zippy-root--qr'
          : isMobileAppFlow
            ? 'zippy-root zippy-root--paying zippy-root--mobile-app'
            : 'zippy-root zippy-root--paying'
        : isTerminal
          ? 'zippy-root zippy-root--terminal'
          : 'zippy-root';

    this.root.innerHTML = '';

    const mount = isTerminal ? this.root : document.createElement('div');
    if (!isTerminal) {
      mount.className = 'zippy-root-body';
      this.root.appendChild(mount);
    }

    if (phase === 'loading') {
      mount.appendChild(this.renderLoading());
      mount.appendChild(this.renderBrandFooter());
      if (showPayProgress) {
        const ratio = session ? getPayWindowRemainingRatio(session) : null;
        if (ratio !== null && ratio > 0) {
          this.root.appendChild(this.renderPayProgress());
        }
      }
      this.applyFocusState(focusState);
      return;
    }

    if (session && !isTerminal) {
      mount.appendChild(this.renderPaymentHero(session, this.heroSubtitle(phase, session)));
    }

    const inlinePaymentExpiryAt = session?.paymentExpiryAt;
    const showInlinePayTimer =
      showPayProgress &&
      phase !== 'qr-active' &&
      phase !== 'mobile-app-active' &&
      inlinePaymentExpiryAt != null &&
      msUntil(inlinePaymentExpiryAt) > 0;

    if (showInlinePayTimer) {
      mount.appendChild(this.renderPayTimer(inlinePaymentExpiryAt));
    }

    switch (phase) {
      case 'choose-method':
        mount.appendChild(this.renderChooseMethod());
        break;
      case 'qr-active':
        mount.appendChild(this.renderQrActive(sessionId, session));
        break;
      case 'mobile-app-active':
        mount.appendChild(this.renderMobileAppActive(session, appHandoffStatus, loading));
        break;
      case 'manual-entry':
        mount.appendChild(
          this.renderManualEntry(paymentId, errorMessage, loading, mobileAppManualFallback),
        );
        break;
      case 'manual-picker':
        mount.appendChild(
          this.renderPayerPicker(verifyMatches ?? [], loading),
        );
        break;
      case 'manual-confirm':
        mount.appendChild(
          this.renderManualConfirm(verifiedPayer, errorMessage, loading),
        );
        break;
      case 'awaiting':
        mount.appendChild(
          this.renderAwaiting(session, committedMethod ?? 'qr', verifiedPayer),
        );
        break;
      case 'success':
      case 'expired':
      case 'cancelled':
      case 'declined':
      case 'failed':
      case 'invalid-session':
        mount.appendChild(this.renderOutcomeScreen(phase, session, exitMode, mode));
        this.mountTerminalExitTimer();
        break;
      case 'error':
        mount.appendChild(
          this.renderTerminal(
            'error',
            'Something went wrong',
            errorMessage ?? 'Please try again or contact the merchant.',
          ),
        );
        break;
      default:
        break;
    }

    if (!isTerminal) {
      mount.appendChild(this.renderBrandFooter());
    } else {
      this.root.appendChild(this.renderBrandFooter());
    }

    if (showPayProgress) {
      const ratio = session ? getPayWindowRemainingRatio(session) : null;
      if (ratio !== null && ratio > 0) {
        this.root.appendChild(this.renderPayProgress());
      }
    }

    this.applyFocusState(focusState);
  }

  private heroSubtitle(phase: CheckoutPhase, session: PaymentSession | null): string {
    switch (phase) {
      case 'choose-method':
        return 'Choose how to pay';
      case 'qr-active':
        return session?.status === 'AWAITING_PAYMENT'
          ? 'Waiting for confirmation'
          : 'Scan with the Zippy app';
      case 'mobile-app-active':
        if (this.state.appHandoffStatus === 'failed') {
          return 'Install or choose another way';
        }
        return session?.status === 'AWAITING_PAYMENT'
          ? 'Waiting for confirmation'
          : 'Pay in the Zippy app';
      case 'manual-entry':
        return 'Find your Zippy account';
      case 'manual-picker':
        return 'Select your account';
      case 'manual-confirm':
        return 'Review and confirm';
      case 'awaiting':
        return 'Waiting for confirmation';
      default:
        return '';
    }
  }

  private renderPaymentHero(session: PaymentSession, subtitle: string): HTMLElement {
    const header = document.createElement('div');
    header.className = 'zippy-payment-hero';

    const business = document.createElement('p');
    business.className = 'zippy-payment-merchant';
    business.textContent = session.businessName;

    const amount = document.createElement('p');
    amount.className = 'zippy-payment-amount';
    amount.textContent = formatAmount(session.amount, session.currency);

    header.appendChild(business);
    header.appendChild(amount);

    if (subtitle) {
      const sub = document.createElement('p');
      sub.className = 'zippy-payment-subtitle';
      sub.textContent = subtitle;
      header.appendChild(sub);
    }

    return header;
  }

  private renderBrandFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'zippy-brand-footer';
    footer.setAttribute('aria-label', 'Zippy checkout footer');

    if (this.uiOptions.surface !== 'phone') {
      const appLinks = document.createElement('div');
      appLinks.className = 'zippy-app-links';

      const appLabel = document.createElement('p');
      appLabel.className = 'zippy-app-links-label';
      appLabel.textContent = 'Get the Zippy Pay app';

      const badges = document.createElement('div');
      badges.className = 'zippy-app-links-badges';

      badges.appendChild(
        this.renderStoreBadgeButton(
          'ios',
          APP_STORE_LINKS.ios,
          'Show App Store download options',
          renderAppleStoreIcon(),
        ),
      );
      badges.appendChild(
        this.renderStoreBadgeButton(
          'android',
          APP_STORE_LINKS.android,
          'Show Google Play download options',
          renderGooglePlayIcon(),
        ),
      );

      appLinks.appendChild(appLabel);
      appLinks.appendChild(badges);
      footer.appendChild(appLinks);
    }

    const inner = document.createElement('div');
    inner.className = 'zippy-brand-footer-inner';

    const secure = document.createElement('span');
    secure.className = 'zippy-brand-secure';
    secure.innerHTML = `${renderSecureLockSvg()}<span class="zippy-brand-copy">Secured by</span>`;

    const divider = document.createElement('span');
    divider.className = 'zippy-brand-divider';
    divider.setAttribute('aria-hidden', 'true');

    const brand = document.createElement('span');
    brand.className = 'zippy-brand-name';
    brand.textContent = 'Zippy';

    inner.appendChild(secure);
    inner.appendChild(divider);
    inner.appendChild(brand);

    footer.appendChild(inner);
    return footer;
  }

  private renderStoreBadgeButton(
    platform: AppStorePlatform,
    url: string,
    ariaLabel: string,
    icon: string,
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `zippy-app-link zippy-app-link--icon-only zippy-app-link--${platform}`;
    button.setAttribute('aria-label', ariaLabel);
    button.setAttribute('aria-haspopup', 'dialog');
    button.innerHTML = icon;
    button.addEventListener('click', () => {
      this.appStoreModal.open(platform, url, button);
    });
    return button;
  }

  private renderLoading(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-status';
    const icon = document.createElement('div');
    icon.className = 'zippy-status-icon loading';
    const spinner = document.createElement('div');
    spinner.className = 'zippy-spinner';
    icon.appendChild(spinner);
    const title = document.createElement('p');
    title.className = 'zippy-status-title';
    title.textContent = 'Loading checkout…';
    wrap.appendChild(icon);
    wrap.appendChild(title);
    return wrap;
  }

  private renderChooseMethod(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-choose';
    const { appHandoffStatus = 'idle' } = this.state;

    const options = document.createElement('div');
    options.className = 'zippy-choose-options';
    const { methodAvailability } = this.uiOptions;

    if (methodAvailability.qr) {
      options.appendChild(
        this.renderMethodCard({
          method: 'qr',
          title: 'QR code',
          description: 'Open Zippy and scan',
          icon: this.qrIconSvg(),
        }),
      );
    }

    if (methodAvailability.manual) {
      options.appendChild(
        this.renderMethodCard({
          method: 'manual',
          title: 'Zippy account',
          description: 'ID, email, or phone',
          icon: this.idIconSvg(),
        }),
      );
    }

    if (methodAvailability.qr || methodAvailability.manual) {
      wrap.appendChild(options);
    }

    if (methodAvailability.mobileApp) {
      const mobileAppCta = document.createElement('div');
      mobileAppCta.className = 'zippy-choose-mobile-app';
      mobileAppCta.appendChild(
        this.renderPayWithZippyButton({
          opening: appHandoffStatus === 'opening',
          action: 'choose',
        }),
      );
      wrap.appendChild(mobileAppCta);
    }

    return wrap;
  }

  private renderMethodCard(params: {
    method: CheckoutMethod;
    title: string;
    description: string;
    icon: string;
  }): HTMLElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = `zippy-method-card zippy-method-card--${params.method}`;
    card.setAttribute(
      'aria-label',
      params.method === 'qr'
        ? 'Pay by scanning a QR code in the Zippy app'
        : params.method === 'mobileApp'
          ? 'Pay with the Zippy app on this phone'
          : 'Pay with your Zippy ID, email, or phone',
    );

    const icon = document.createElement('span');
    icon.className = 'zippy-method-icon';
    icon.innerHTML = params.icon;

    const copy = document.createElement('span');
    copy.className = 'zippy-method-copy';

    const title = document.createElement('span');
    title.className = 'zippy-method-title';
    title.textContent = params.title;

    const description = document.createElement('span');
    description.className = 'zippy-method-hint';
    description.textContent = params.description;

    copy.appendChild(title);
    copy.appendChild(description);
    card.appendChild(icon);
    card.appendChild(copy);
    card.addEventListener('click', () => this.callbacks.onChooseMethod(params.method));

    return card;
  }

  private renderQrActive(sessionId: string, session: PaymentSession | null): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-flow zippy-flow--centered';
    const isAwaiting = session?.status === 'AWAITING_PAYMENT';

    const qrWrap = document.createElement('div');
    qrWrap.className = 'zippy-qr-section';
    const frame = document.createElement('div');
    frame.className = 'zippy-qr-frame';
    frame.innerHTML = renderPaymentQrMarkup(sessionId);
    qrWrap.appendChild(frame);

    if (session?.paymentExpiryAt && msUntil(session.paymentExpiryAt) > 0) {
      qrWrap.appendChild(this.renderPayTimer(session.paymentExpiryAt));
    }

    if (isAwaiting) {
      qrWrap.appendChild(this.renderQrAwaitingLoader());
    }

    wrap.appendChild(qrWrap);

    if (isAwaiting) {
      if (session?.payer) {
        wrap.appendChild(this.renderIdentityCard(session.payer));
      }
      wrap.appendChild(
        this.renderFootnote('Complete the payment in the Zippy app to finish checkout.'),
      );
    } else {
      wrap.appendChild(
        this.renderFlowHint('Open Zippy Pay, scan the code, and complete your payment.'),
      );
      wrap.appendChild(this.renderFootnote('Keep this page open until payment completes.'));
    }

    return wrap;
  }

  private renderMobileAppActive(
    session: PaymentSession | null,
    appHandoffStatus: AppHandoffStatus,
    loading: boolean,
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-flow zippy-flow--centered';
    const isAwaiting = session?.status === 'AWAITING_PAYMENT';

    const section = document.createElement('div');
    section.className = 'zippy-mobile-app-section';

    const paymentExpiryAt = session?.paymentExpiryAt;
    const showInlineTimer =
      paymentExpiryAt != null &&
      msUntil(paymentExpiryAt) > 0 &&
      appHandoffStatus !== 'idle' &&
      appHandoffStatus !== 'opening';

    if (showInlineTimer) {
      section.appendChild(this.renderPayTimer(paymentExpiryAt));
    }

    if (appHandoffStatus === 'opening') {
      section.appendChild(this.renderPayWithZippyButton({ opening: true }));
    } else if (appHandoffStatus === 'failed') {
      if (loading) {
        section.appendChild(this.renderMobileAppMethodSwitching());
      } else {
        section.appendChild(this.renderMobileAppHandoffFailed());
      }
    } else if (appHandoffStatus === 'opened' || isAwaiting) {
      if (isAwaiting) {
        section.appendChild(this.renderQrAwaitingLoader());
      } else {
        section.appendChild(this.renderMobileAppOpenedHint());
      }
    } else {
      section.appendChild(this.renderPayWithZippyButton({ disabled: isAwaiting }));
    }

    wrap.appendChild(section);

    if (isAwaiting) {
      if (session?.payer) {
        wrap.appendChild(this.renderIdentityCard(session.payer));
      }
      wrap.appendChild(
        this.renderFootnote('Complete the payment in the Zippy app to finish checkout.'),
      );
    } else if (appHandoffStatus === 'failed') {
      wrap.appendChild(
        this.renderFootnote(
          loading
            ? 'Switching payment method…'
            : 'Install Zippy or choose another payment method below.',
        ),
      );
    } else {
      wrap.appendChild(
        this.renderFootnote('Keep this page open until payment completes.'),
      );
    }

    return wrap;
  }

  private renderMobileAppMethodSwitching(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-mobile-app-method-switching';
    wrap.setAttribute('aria-live', 'polite');
    wrap.setAttribute('aria-busy', 'true');

    const spinner = document.createElement('span');
    spinner.className = 'zippy-qr-awaiting-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    const title = document.createElement('p');
    title.className = 'zippy-mobile-app-method-switching-title';
    title.textContent = 'Switching payment method…';

    wrap.appendChild(spinner);
    wrap.appendChild(title);
    return wrap;
  }

  private renderPayWithZippyButton(
    options: {
      disabled?: boolean;
      opening?: boolean;
      action?: 'choose' | 'open';
    } = {},
  ): HTMLButtonElement {
    const { disabled = false, opening = false, action = 'open' } = options;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'zippy-mobile-app-pay-btn';
    button.disabled = disabled || opening;

    if (opening) {
      button.classList.add('zippy-mobile-app-pay-btn--opening');
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-live', 'polite');
      button.setAttribute('aria-label', 'Opening Zippy Pay');

      const spinner = document.createElement('span');
      spinner.className =
        'zippy-qr-awaiting-spinner zippy-mobile-app-pay-btn-spinner';
      spinner.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.textContent = 'Opening Zippy…';

      button.appendChild(spinner);
      button.appendChild(label);
    } else {
      button.textContent = 'Pay with Zippy';
      button.setAttribute('aria-label', 'Open Zippy Pay to complete this payment');
    }

    button.addEventListener('click', () => {
      if (button.disabled) {
        return;
      }
      if (action === 'choose') {
        this.callbacks.onChooseMethod('mobileApp');
      } else {
        this.callbacks.onOpenMobileApp?.();
      }
    });
    return button;
  }

  private renderMobileAppOpenedHint(): HTMLElement {
    const hint = document.createElement('p');
    hint.className = 'zippy-mobile-app-opened-hint';
    hint.textContent = 'Complete your payment in the Zippy app.';
    return hint;
  }

  private renderMobileAppHandoffFailed(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-mobile-app-handoff-failed';

    const message = document.createElement('p');
    message.className = 'zippy-mobile-app-handoff-message';
    message.textContent = "We couldn't open the Zippy app on this device.";

    const install = document.createElement('button');
    install.type = 'button';
    install.className = 'zippy-install-zippy-btn';
    install.textContent = 'Install Zippy';
    install.setAttribute('aria-label', 'Install Zippy Pay from your app store');
    install.addEventListener('click', () => {
      this.callbacks.onInstallZippy?.();
    });

    wrap.appendChild(message);
    wrap.appendChild(this.renderRetryMobileAppLink());
    wrap.appendChild(this.renderMobileAppFallbackOptions());
    wrap.appendChild(install);

    return wrap;
  }

  private renderMobileAppFallbackOptions(): HTMLElement | DocumentFragment {
    const { checkoutPolicy } = this.uiOptions;
    const fallback = resolveFallbackMethodAvailability(checkoutPolicy);

    if (!fallback.qr && !fallback.manual) {
      return document.createDocumentFragment();
    }

    const wrap = document.createElement('div');
    wrap.className = 'zippy-mobile-app-fallback';

    const label = document.createElement('p');
    label.className = 'zippy-mobile-app-fallback-label';
    label.textContent = 'Or pay another way';

    const options = document.createElement('div');
    options.className = 'zippy-choose-options zippy-choose-options--compact';

    if (fallback.qr) {
      options.appendChild(
        this.renderMethodCard({
          method: 'qr',
          title: 'QR code',
          description: 'Open Zippy and scan',
          icon: this.qrIconSvg(),
        }),
      );
    }

    if (fallback.manual) {
      options.appendChild(
        this.renderMethodCard({
          method: 'manual',
          title: 'Zippy account',
          description: 'ID, email, or phone',
          icon: this.idIconSvg(),
        }),
      );
    }

    wrap.appendChild(label);
    wrap.appendChild(options);
    return wrap;
  }

  private renderRetryMobileAppLink(): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'zippy-mobile-app-retry';
    button.textContent = 'Try again';
    button.setAttribute('aria-label', 'Try opening the Zippy app again');
    button.addEventListener('click', () => {
      this.callbacks.onRetryMobileApp?.();
    });
    return button;
  }

  private renderQrAwaitingLoader(): HTMLElement {
    const slot = document.createElement('div');
    slot.className = 'zippy-qr-awaiting';
    slot.setAttribute('aria-live', 'polite');
    slot.setAttribute('aria-label', 'Waiting for payment confirmation');

    const spinner = document.createElement('span');
    spinner.className = 'zippy-qr-awaiting-spinner';
    spinner.setAttribute('aria-hidden', 'true');

    slot.appendChild(spinner);
    return slot;
  }

  private renderPayTimer(expiresAt: string): HTMLElement {
    const slot = document.createElement('div');
    slot.className = 'zippy-pay-timer-slot';

    const remainingMs = msUntil(expiresAt);
    const timer = document.createElement('div');
    timer.className = 'zippy-pay-timer';
    timer.setAttribute('role', 'timer');
    timer.setAttribute('aria-live', 'polite');
    timer.setAttribute(
      'aria-label',
      `${formatCountdownAccessible(remainingMs)} remaining to complete payment`,
    );

    const label = document.createElement('span');
    label.className = 'zippy-pay-timer-label';
    label.textContent = 'Time remaining';

    const value = document.createElement('span');
    value.className = 'zippy-pay-timer-value';
    value.setAttribute('aria-hidden', 'true');
    value.textContent = formatCountdown(remainingMs);

    timer.appendChild(label);
    timer.appendChild(value);
    slot.appendChild(timer);
    return slot;
  }

  private renderManualEntry(
    query: string,
    errorMessage: string | null,
    loading: boolean,
    mobileAppManualFallback: boolean,
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-flow';

    if (mobileAppManualFallback && this.callbacks.onBackToMobileAppFallback) {
      wrap.appendChild(
        this.renderBackButton(loading, {
          label: 'Back',
          onClick: () => this.callbacks.onBackToMobileAppFallback?.(),
        }),
      );
    } else if (this.uiOptions.allowManualBack && this.callbacks.onBackToChooseMethod) {
      wrap.appendChild(this.renderBackButton(loading, { label: 'Back' }));
    }

    const form = document.createElement('form');
    form.className = 'zippy-id-form';
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!loading && input.value.trim()) {
        void this.callbacks.onVerify(input.value);
      }
    });

    const field = document.createElement('div');
    field.className = 'zippy-input-combo';

    const inputWrap = document.createElement('div');
    inputWrap.className = 'zippy-input-combo-input-wrap';

    const input = document.createElement('input');
    input.id = 'zippy-payer-query';
    input.className = 'zippy-input-combo-field';
    input.type = 'text';
    input.placeholder = 'Zippy ID, email, or +15551234567';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.inputMode = 'text';
    input.value = query;
    input.disabled = loading;
    input.setAttribute('aria-label', 'Zippy ID, email, or phone number');

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'zippy-input-combo-clear';
    clearBtn.setAttribute('aria-label', 'Clear input');
    clearBtn.innerHTML =
      '<svg class="zippy-input-combo-clear-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

    const divider = document.createElement('span');
    divider.className = 'zippy-input-combo-divider';
    divider.setAttribute('aria-hidden', 'true');

    const verifyBtn = document.createElement('button');
    verifyBtn.type = 'button';
    verifyBtn.className = 'zippy-input-combo-action';
    verifyBtn.textContent = 'Find';
    verifyBtn.setAttribute('aria-label', 'Find Zippy account');

    const syncActionState = (): void => {
      const trimmed = input.value.trim();
      const isVerifying = loading;
      const hasValue = input.value.length > 0 && !loading;
      inputWrap.classList.toggle('zippy-input-combo-input-wrap--has-value', hasValue);
      clearBtn.disabled = !hasValue;
      clearBtn.tabIndex = hasValue ? 0 : -1;
      clearBtn.setAttribute('aria-hidden', hasValue ? 'false' : 'true');
      verifyBtn.disabled = loading || trimmed.length === 0;
      verifyBtn.classList.toggle('zippy-input-combo-action--loading', isVerifying);

      const existingSpinner = verifyBtn.querySelector('.zippy-spinner-inline');
      if (isVerifying) {
        verifyBtn.textContent = '';
        if (!existingSpinner) {
          const spinner = document.createElement('span');
          spinner.className = 'zippy-spinner-inline';
          spinner.setAttribute('aria-hidden', 'true');
          verifyBtn.appendChild(spinner);
        }
        verifyBtn.setAttribute('aria-label', 'Finding account');
      } else {
        existingSpinner?.remove();
        verifyBtn.textContent = 'Find';
        verifyBtn.setAttribute('aria-label', 'Find Zippy account');
      }
    };

    verifyBtn.addEventListener('click', () => {
      void this.callbacks.onVerify(input.value);
    });

    clearBtn.addEventListener('click', () => {
      input.value = '';
      this.state.paymentId = '';
      this.callbacks.onPaymentIdChange?.('');
      input.focus();
      syncActionState();
    });

    input.addEventListener('input', () => {
      this.state.paymentId = input.value;
      this.callbacks.onPaymentIdChange?.(input.value);
      syncActionState();
    });

    syncActionState();

    if (errorMessage) {
      field.classList.add('zippy-input-combo--error');
    }

    field.appendChild(inputWrap);
    inputWrap.appendChild(input);
    inputWrap.appendChild(clearBtn);
    field.appendChild(divider);
    field.appendChild(verifyBtn);
    form.appendChild(field);

    if (errorMessage) {
      form.appendChild(this.renderFieldError(errorMessage));
    }

    form.appendChild(this.renderPayerQueryHint());

    wrap.appendChild(form);
    return wrap;
  }

  private renderPayerQueryHint(): HTMLElement {
    const hint = document.createElement('div');
    hint.className = 'zippy-query-hint';

    const lead = document.createElement('p');
    lead.className = 'zippy-query-hint-lead';
    lead.textContent = 'Find your account using any of:';

    const list = document.createElement('ul');
    list.className = 'zippy-query-hint-list';

    for (const item of [
      { label: 'Zippy ID', example: 'john.doe12' },
      { label: 'Email', example: 'you@example.com' },
      { label: 'Phone', example: '+15551234567', note: 'E.164 — include + and country code' },
    ]) {
      list.appendChild(this.renderPayerQueryHintItem(item));
    }

    hint.appendChild(lead);
    hint.appendChild(list);
    return hint;
  }

  private renderPayerQueryHintItem(item: {
    label: string;
    example: string;
    note?: string;
  }): HTMLLIElement {
    const row = document.createElement('li');
    row.className = 'zippy-query-hint-item';

    const label = document.createElement('span');
    label.className = 'zippy-query-hint-label';
    label.textContent = item.label;

    const exampleWrap = document.createElement('span');
    exampleWrap.className = 'zippy-query-hint-example-wrap';

    const example = document.createElement('code');
    example.className = 'zippy-query-hint-example';
    example.textContent = item.example;
    exampleWrap.appendChild(example);

    if (item.note) {
      const note = document.createElement('span');
      note.className = 'zippy-query-hint-note';
      note.textContent = item.note;
      exampleWrap.appendChild(note);
    }

    row.appendChild(label);
    row.appendChild(exampleWrap);
    return row;
  }

  private renderPayerPicker(matches: PayerPreview[], loading: boolean): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-flow';

    if (this.callbacks.onBackToManualEntry) {
      wrap.appendChild(
        this.renderBackButton(loading, {
          label: 'Back',
          onClick: () => this.callbacks.onBackToManualEntry?.(),
        }),
      );
    }

    const lead = document.createElement('p');
    lead.className = 'zippy-picker-lead';
    lead.textContent = 'Multiple accounts match. Select the one paying for this checkout.';
    wrap.appendChild(lead);

    const list = document.createElement('div');
    list.className = 'zippy-picker-list';
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Matching Zippy accounts');

    for (const payer of matches) {
      list.appendChild(this.renderPayerPickerOption(payer, loading));
    }

    wrap.appendChild(list);
    return wrap;
  }

  private renderPayerPickerOption(payer: PayerPreview, loading: boolean): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'zippy-picker-option';
    button.disabled = loading;
    button.setAttribute('role', 'option');
    button.setAttribute('aria-label', `Pay as ${payer.displayName}`);

    button.appendChild(this.renderIdentityCard(payer));
    button.addEventListener('click', () => {
      this.callbacks.onSelectPayer?.(payer);
    });

    return button;
  }

  private renderManualConfirm(
    verifiedPayer: PayerPreview | null,
    errorMessage: string | null,
    loading: boolean,
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-flow';

    const proceeding = loading && Boolean(verifiedPayer);

    if (verifiedPayer) {
      wrap.appendChild(
        this.renderIdentityCard(verifiedPayer, {
          dismissible: true,
          dismissDisabled: proceeding,
          onDismiss: () => this.callbacks.onBackToManualEntry?.(),
        }),
      );
    }

    if (errorMessage) {
      wrap.appendChild(this.renderFieldError(errorMessage));
    }

    const payBtn = document.createElement('button');
    payBtn.type = 'button';
    payBtn.className = 'zippy-btn zippy-btn-primary zippy-btn-block';
    payBtn.textContent = proceeding ? 'Processing…' : 'Proceed';
    payBtn.disabled = loading || !verifiedPayer;
    payBtn.addEventListener('click', () => {
      if (verifiedPayer) {
        void this.callbacks.onPay(verifiedPayer.paymentId);
      }
    });

    wrap.appendChild(payBtn);
    return wrap;
  }

  private renderBackButton(
    disabled: boolean,
    options?: { label?: string; onClick?: () => void },
  ): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'zippy-back-btn';
    button.disabled = disabled;
    button.setAttribute('aria-label', options?.label ?? 'Back to payment options');
    button.innerHTML = `<svg class="zippy-back-btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M14.5 6.5 9 12l5.5 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>${options?.label ?? 'Back'}</span>`;
    button.addEventListener('click', () => {
      if (options?.onClick) {
        options.onClick();
      } else {
        this.callbacks.onBackToChooseMethod?.();
      }
    });
    return button;
  }

  private renderFieldError(message: string): HTMLElement {
    const error = document.createElement('p');
    error.className = 'zippy-field-error';
    error.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'zippy-field-error-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '!';

    const copy = document.createElement('span');
    copy.textContent = message;

    error.appendChild(icon);
    error.appendChild(copy);
    return error;
  }

  private renderIdentityCard(
    payer: PaymentSessionPayer,
    options?: {
      dismissible?: boolean;
      dismissDisabled?: boolean;
      onDismiss?: () => void;
    },
  ): HTMLElement {
    const identity = document.createElement('div');
    identity.className = `zippy-identity-card${
      options?.dismissible ? ' zippy-identity-card--dismissible' : ''
    }`;

    const avatar = this.renderIdentityAvatar(payer);

    const copy = document.createElement('div');
    copy.className = 'zippy-identity-copy';

    const label = document.createElement('p');
    label.className = 'zippy-identity-label';
    label.textContent = 'Paying as';

    const name = document.createElement('p');
    name.className = 'zippy-identity-name';
    name.textContent = payer.displayName;

    const handle = document.createElement('p');
    handle.className = 'zippy-identity-handle';
    handle.textContent = payer.paymentId;

    copy.appendChild(label);
    copy.appendChild(name);
    copy.appendChild(handle);
    identity.appendChild(avatar);
    identity.appendChild(copy);

    if (options?.dismissible) {
      identity.appendChild(
        this.renderCardDismissButton(options.dismissDisabled ?? false, options.onDismiss),
      );
    }

    return identity;
  }

  private renderCardDismissButton(disabled: boolean, onDismiss?: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'zippy-identity-card-dismiss';
    button.disabled = disabled;
    button.setAttribute('aria-label', 'Change account');
    button.innerHTML =
      '<svg class="zippy-identity-card-dismiss-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    button.addEventListener('click', () => {
      onDismiss?.();
    });
    return button;
  }

  private renderIdentityAvatar(payer: PaymentSessionPayer): HTMLElement {
    const avatar = document.createElement('div');
    avatar.className = 'zippy-identity-avatar';
    const initials = formatDisplayInitials(payer.displayName);
    const profileImage = payer.profileImage?.trim();

    if (profileImage) {
      avatar.classList.add('zippy-identity-avatar--photo');
      const img = document.createElement('img');
      img.className = 'zippy-identity-avatar-image';
      img.src = profileImage;
      img.alt = '';
      img.addEventListener('error', () => {
        avatar.classList.remove('zippy-identity-avatar--photo');
        img.remove();
        avatar.textContent = initials;
      });
      avatar.appendChild(img);
    } else {
      avatar.textContent = initials;
    }

    return avatar;
  }

  private renderAwaiting(
    session: PaymentSession | null,
    method: CheckoutMethod,
    verifiedPayer: PayerPreview | null,
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-flow zippy-flow-awaiting';

    const status = document.createElement('div');
    status.className = 'zippy-awaiting-status';
    status.innerHTML = `<div class="zippy-awaiting-ring" aria-hidden="true"><span></span></div>`;
    wrap.appendChild(status);

    const payer = session?.payer ?? verifiedPayer ?? null;
    if (payer) {
      wrap.appendChild(this.renderIdentityCard(payer));
    }

    const message = document.createElement('p');
    message.className = 'zippy-awaiting-message';
    message.textContent =
      method === 'qr'
        ? 'Complete the payment in the Zippy app to finish checkout.'
        : method === 'mobileApp'
          ? 'Complete the payment in the Zippy app to finish checkout.'
          : 'Tap the notification in Zippy Pay to approve this payment.';
    wrap.appendChild(message);

    wrap.appendChild(this.renderFootnote('Do not close or refresh this page.'));
    return wrap;
  }

  private renderFlowHint(text: string): HTMLElement {
    const hint = document.createElement('p');
    hint.className = 'zippy-flow-hint';
    hint.textContent = text;
    return hint;
  }

  private renderPayProgress(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'zippy-pay-progress';
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-label', 'Payment time remaining');
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');

    const fill = document.createElement('div');
    fill.className = 'zippy-pay-progress-fill';
    bar.appendChild(fill);
    this.mountPayProgress(fill, bar);
    return bar;
  }

  private mountPayProgress(fill: HTMLElement, bar: HTMLElement): void {
    const tick = (): void => {
      const session = this.state.session;
      if (!session) {
        return;
      }

      const ratio = getPayWindowRemainingRatio(session);
      if (ratio === null || ratio <= 0) {
        bar.style.display = 'none';
        bar.setAttribute('aria-valuenow', '0');
        this.clearExpiryTimer();
        return;
      }

      bar.style.display = '';
      const pct = Math.round(ratio * 100);
      fill.style.transform = `scaleX(${ratio})`;
      bar.setAttribute('aria-valuenow', String(pct));

      const countdownEl = this.shadow.querySelector('.zippy-pay-timer-value');
      const timerEl = this.shadow.querySelector('.zippy-pay-timer');
      if (countdownEl instanceof HTMLElement && session.paymentExpiryAt) {
        const remainingMs = msUntil(session.paymentExpiryAt);
        countdownEl.textContent = formatCountdown(remainingMs);
        if (timerEl instanceof HTMLElement) {
          timerEl.setAttribute(
            'aria-label',
            `${formatCountdownAccessible(remainingMs)} remaining to complete payment`,
          );
        }
      }
    };

    tick();
    this.expiryTimer = setInterval(tick, 1000);
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer !== null) {
      clearInterval(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private mountTerminalExitTimer(): void {
    this.clearTerminalExitTimer();
    let remaining = TERMINAL_EXIT_COUNTDOWN_SEC;

    const updateDisplay = (): void => {
      const countdownEl = this.shadow.querySelector('.zippy-status-exit-countdown');
      if (countdownEl instanceof HTMLElement) {
        countdownEl.textContent = String(remaining);
      }
    };

    updateDisplay();
    this.terminalExitTimer = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.clearTerminalExitTimer();
        this.callbacks.onTerminalExit?.();
        return;
      }
      updateDisplay();
    }, 1000);
  }

  private clearTerminalExitTimer(): void {
    if (this.terminalExitTimer !== null) {
      clearInterval(this.terminalExitTimer);
      this.terminalExitTimer = null;
    }
  }

  private renderFootnote(text: string): HTMLElement {
    const footnote = document.createElement('p');
    footnote.className = 'zippy-footnote';
    footnote.textContent = text;
    return footnote;
  }

  private renderOutcomeScreen(
    phase: CheckoutPhase,
    session: PaymentSession | null,
    exitMode: TerminalExitMode | null | undefined,
    embedMode: 'inline' | 'modal',
  ): HTMLElement {
    const copy = outcomeTerminalCopy(phase);
    return this.renderOutcomeStatus(
      copy.kind,
      copy.title,
      copy.detail,
      copy.kind,
      exitMode,
      embedMode,
      session?.transactionId ?? null,
    );
  }

  private renderOutcomeStatus(
    kind: 'success' | 'failure',
    titleText: string,
    detailText: string,
    outcome: 'success' | 'failure',
    exitMode: TerminalExitMode | null | undefined,
    embedMode: 'inline' | 'modal',
    transactionId: string | null,
  ): HTMLElement {
    const wrap = this.renderTerminal(kind, titleText, detailText);

    if (transactionId) {
      wrap.appendChild(this.renderTransactionReference(transactionId));
    }

    const resolvedExitMode = exitMode ?? 'close';
    const exit = document.createElement('p');
    exit.className = 'zippy-status-exit';

    const lead = document.createElement('span');
    lead.className = 'zippy-status-exit-lead';
    lead.textContent = terminalExitLeadText(resolvedExitMode, embedMode, outcome);

    const countdown = document.createElement('span');
    countdown.className = 'zippy-status-exit-countdown';
    countdown.textContent = String(TERMINAL_EXIT_COUNTDOWN_SEC);

    const suffix = document.createElement('span');
    suffix.className = 'zippy-status-exit-suffix';
    suffix.textContent = ' seconds…';

    exit.appendChild(lead);
    exit.appendChild(document.createTextNode(' '));
    exit.appendChild(countdown);
    exit.appendChild(suffix);
    wrap.appendChild(exit);

    return wrap;
  }

  private renderTransactionReference(transactionId: string): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-transaction-ref';

    const label = document.createElement('p');
    label.className = 'zippy-transaction-ref-label';
    label.textContent = 'Transaction ID';

    const value = document.createElement('p');
    value.className = 'zippy-transaction-ref-value';
    value.textContent = transactionId;

    wrap.appendChild(label);
    wrap.appendChild(value);
    return wrap;
  }

  private renderTerminal(
    kind: 'success' | 'failure' | 'error',
    titleText: string,
    detailText: string,
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'zippy-status';

    const icon = document.createElement('div');
    icon.className = `zippy-status-icon ${kind}`;
    icon.innerHTML =
      kind === 'success'
        ? `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M7 14.5l4.5 4.5L21 9.5" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`
        : kind === 'failure'
          ? `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M10 10l8 8M18 10l-8 8" stroke="currentColor" stroke-width="2.25" stroke-linecap="round"/></svg>`
          : `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M10 10l8 8M18 10l-8 8" stroke="currentColor" stroke-width="2.25" stroke-linecap="round"/></svg>`;

    const title = document.createElement('p');
    title.className = 'zippy-status-title';
    title.textContent = titleText;

    const detail = document.createElement('p');
    detail.className = 'zippy-status-detail';
    detail.textContent = detailText;

    wrap.appendChild(icon);
    wrap.appendChild(title);
    wrap.appendChild(detail);
    return wrap;
  }

  private qrIconSvg(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.25" stroke="currentColor" stroke-width="1.6"/>
      <rect x="14" y="3" width="7" height="7" rx="1.25" stroke="currentColor" stroke-width="1.6"/>
      <rect x="3" y="14" width="7" height="7" rx="1.25" stroke="currentColor" stroke-width="1.6"/>
      <rect x="14" y="14" width="3" height="3" rx="0.5" fill="currentColor"/>
      <rect x="18" y="14" width="3" height="3" rx="0.5" fill="currentColor"/>
      <rect x="14" y="18" width="3" height="3" rx="0.5" fill="currentColor"/>
      <rect x="18" y="18" width="3" height="3" rx="0.5" fill="currentColor"/>
    </svg>`;
  }

  private idIconSvg(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="9" r="3.5" stroke="currentColor" stroke-width="1.6"/>
      <path d="M5 20c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    </svg>`;
  }
}
